import { useRef, useState, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ReactSVG } from "react-svg";

import Button from "../../../../common/Button";
import NewToolTip from "../../../../common/New-ToolTip";
import CustomInput from "../../../../common/CustomInput";
import {
  dispenseOptions,
  dispenseTabletOptions,
  reasonOptions,
  PRN_FREQUENCY_VALUES,
  recentStartOptions,
  unitOptions,
  medicationTypeOptions,
  specialInstructionOptions,
  drugTypes,
  vaccineDoseTypeOptions,
  vaccineReasonOptions,
  totalVaccineDoseOptions,
  therapyTypeOptions,
} from "./data";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";
import {
  createMedication,
  updateMedication,
} from "../../../../../redux/modules/clinical-services/actions";
import { useDispatch, useSelector } from "react-redux";
import { setMedicationInitialData } from "../../../../../redux/modules/patient/actions";
import Alert from "../../../../common/Alert";
import CommonSlider from "../../../../common/CommonSlider";
import MedicationSelect from "../../services/select/medication-select";
import DosageSelect from "../../services/select/dosage-select";
import FormSelect from "../../services/select/form-select";
import RouteSelect from "../../services/select/route-select";
import FrequencySelect from "../../services/select/frequency-select";
import PRNSelect from "../../services/select/prn-select";
import CommonSelect from "../../services/select/common-creatable-select";
import { isValidVigilanceSelection } from "../../../../../helpers/input-validations";
import InformationStep from "./treatment/information-step";
import MedicalConditionSelect from "../../services/select/medical-condition";
import RecentStartToolTip from "../../../../common/tooltip/recent-start";
import { scrollY } from "../../../../../helpers/functions";
import { nonPharmacologicalTypes } from "../treatment/non-pharmacological/data";
import AddMedicationDialog from "../../medications/AddMedicationDialog";

// Common Medication Type Tooltip Component
const MedicationTypeTooltip = () => (
  <div className="text-teal-300 p-4 bg-gray-10">
    <p className="text-base">Medication Type</p>
    <div className="border-b-[7px] border-gray-20 mb-[6px]"></div>
    <p>
      <span className="font-semibold">Prescribed Rx - Pending Dispense{" "}</span>
      - Prescriptions issued during a Minor Ailment assessment,<br /> but not yet confirmed as dispensed. <br />
      Status will update in REEL automatically once pharmacy system integration is enabled.
    </p>
  </div>
);

const TREATMENT_PRESCRIPTION_NOTES_KEY = "treatmentPrescriptionNotes";
const getTherapyTypeOption = (value) =>
  therapyTypeOptions.find((option) => option.value === value) || null;
const getMedicationNotesKey = (id, medicationName) =>
  id != null && id !== "" ? `id:${id}` : `name:${String(medicationName || "").trim().toLowerCase()}`;
const readPrescriptionNotesMap = () => {
  try {
    const raw = sessionStorage.getItem(TREATMENT_PRESCRIPTION_NOTES_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};
const savePrescriptionNotesOverride = ({
  medicationId,
  medicationName,
  includeOnPrescription,
  commentNotes,
}) => {
  const notesMap = readPrescriptionNotesMap();
  const overridePayload = {
    includeOnPrescription: Boolean(includeOnPrescription),
    commentNotes: (commentNotes || "").trim(),
  };
  notesMap[getMedicationNotesKey(medicationId, medicationName)] = overridePayload;
  if (medicationName) {
    notesMap[getMedicationNotesKey(null, medicationName)] = overridePayload;
  }
  sessionStorage.setItem(
    TREATMENT_PRESCRIPTION_NOTES_KEY,
    JSON.stringify(notesMap)
  );
  window.dispatchEvent(new Event("treatmentPrescriptionNotesChanged"));
};

// Helper function to get filtered medication type options (excludes PrescribedRx for non-MA contexts)
const getFilteredMedicationTypeOptions = (currentValue, isMAContext = false) => {
  // Filters out PrescribedRx for non-MA contexts (MA uses static field instead). Exception: include PrescribedRx when editing existing non-MA records.
  const filtered = medicationTypeOptions.filter(option => option.value !== drugTypes.PrescribedRx);
  
  // If current value is PrescribedRx (editing existing non-MA record), include it
  if (currentValue?.value === drugTypes.PrescribedRx && !isMAContext) {
    return medicationTypeOptions;
  }
  
  return filtered;
};

export default function MedicationCreate({
  open,
  onClose,
  onSkip,
  onSave,
  onSaveAndAddAnother = null,
  title = null,
  step = null,
  maxSteps = null,
  medicationName = null,
  fullMedication = null,
  patientId,
  create = true,
  subType = null,
  customSubmit = false,
  customSubmitAction = () => {},
  informationStep = false,
  showDispenseQtyAndRefills = false,
  selectedDrugType = null,
  allMedications = [],
  isRxService = false,
  isMATreatment = false,
  isIntervention = false,
  initialData = null,
  showSaveAndAddAnother = true,
  /** Annual Medication Review vaccine update slider — Figma 5630:75837 field set only. */
  vaccineAnnualReviewLayout = false,
  /** When false, successful save does not call onClose (e.g. multi-vaccine queue: advance in parent). Default true. */
  closeOnSave = true,
}) {
  const dispatch = useDispatch();
  const medicationInitialData = useSelector((state) => state.patients?.medicationInitialData);
  const scrollContainerRef = useRef(null);
  const formBodyRef = useRef(null);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
        if (formBodyRef.current) {
          formBodyRef.current.scrollTop = 0;
        }
      });
    }
  }, [open]);

  const [clicked, setClicked] = useState(false);
  const initialMedicine = {
    doseOptions: [],
    dose: null,
    formOptions: [],
    form: null,
    routeOptions: [],
    route: null,
    prn: false,
    prnDetail: null,
    frequency: null,
  };
  const [selectedMedicine, setSelectedMedicine] = useState({
    doseOptions: [],
    dose: null,
    formOptions: [],
    form: null,
    routeOptions: [],
    route: null,
  });
  const [selectedDose, setSelectedDose] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [selectedFrequency, setSelectedFrequency] = useState(null);
  const [selectedPRN, setSelectedPRN] = useState(null);
  const [unit, setUnit] = useState(null);
  const [duration, setDuration] = useState("");
  const [prn, setPrn] = useState(false);
  const [sig, setSig] = useState("");
  const [reason, setReason] = useState(null);
  const [therapyType, setTherapyType] = useState(null);
  const [activeButton, setActiveButton] = useState("adult");

  const [recentStart, setRecentStart] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [creator, setCreator] = useState(null);
  const [createCommunicationType, setCreateCommunicationType] = useState(null);
  const [prescriberName, setPrescriberName] = useState("");
  const [medicationType, setMedicationType] = useState(null);
  const [comments, setComments] = useState(null);
  const [customSpecialInstruction, setCustomSpecialInstruction] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [commentNotes, setCommentNotes] = useState("");
  const [includeOnPrescription, setIncludeOnPrescription] = useState(false);
  const [created, setCreated] = useState(new Date());
  const [status, setStatus] = useState("active");
  const [isRx, setIsRx] = useState(
    isRxService || selectedDrugType === drugTypes.PrescribedRx
  );

  const LabelWithTooltip = ({ text, tooltipId, required }) => (
    <span className="flex items-center gap-2">
      <span >
        {text}
        {required ? <span className="ml-1">*</span> : null}
      </span>
      {tooltipId ? (
        <span data-tooltip-id={tooltipId}>
          <ReactSVG src="/icons/information-dark.svg" className="w-4 h-4 text-teal-300" />
        </span>
      ) : null}
    </span>
  );

  // Helper to check if this is MA Non-Pharma/Pharma context
  const isMAContext = isMATreatment === true;
  const hasCommentNotes = (commentNotes || "").trim().length > 0;
  const isOtherSpecialInstructionSelected = comments?.value === "Other";
  const isOtherReasonSelected = reason?.value === "Other";
  const resolvedSpecialInstruction =
    isOtherSpecialInstructionSelected && customSpecialInstruction.trim()
      ? customSpecialInstruction.trim()
      : comments?.value;

  // Helper component for non-MA tooltip content
  const NonMATooltipContent = () => (
    <div className="text-teal-600 p-4 bg-gray-10 border border-gray-200">
      <p className="text-base">Medication Type</p>
      <div className="border-b-[7px] border-gray-20 mb-[4px]"></div>
      <p>
        <span
            className={`font-bold ${
            medicationType?.value === drugTypes.Prescription && "underline"
          }`}
        >
          Prescriptions
        </span>{" "}
        - Medications filled at the current pharmacy.{" "}
      </p>
      <p>
        <span
            className={`font-bold ${
            medicationType?.value === drugTypes.Additional && "underline"
          }`}
        >
          Additional Medications{" "}
        </span>
        - OTC, samples, study drugs, {" "}
        <br />
        Natural Products or Prescriptions filled at alternate pharmacy.
      </p>
    </div>
  );

  // Derive strength from the pre-selected medicine's identifier (format: "DIN##STRENGTH##FORM")
  const selectedMedicineStrength = (() => {
    const identifier = selectedMedicine?.value || selectedMedicine?.identifier;
    if (!identifier) return null;
    const parts = String(identifier).split("##");
    return  parts[1] || null;
  })();

  const adjustCommentNotesHeight = (textarea) => {
    if (!textarea) return;
    // Force browser baseline to one row before measuring to avoid default 2-row textarea sizing.
    textarea.rows = 1;

    const computedStyles = window.getComputedStyle(textarea);
    const parsedLineHeight = parseFloat(computedStyles.lineHeight);
    const fontSize = parseFloat(computedStyles.fontSize) || 14;
    const lineHeight = Number.isFinite(parsedLineHeight)
      ? parsedLineHeight
      : Math.ceil(fontSize * 1.43);
    const paddingTop = parseFloat(computedStyles.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyles.paddingBottom) || 0;
    const borderTop = parseFloat(computedStyles.borderTopWidth) || 0;
    const borderBottom = parseFloat(computedStyles.borderBottomWidth) || 0;
    const verticalPadding = paddingTop + paddingBottom;
    const verticalBorder = borderTop + borderBottom;
    const minHeight = Math.ceil(lineHeight + verticalPadding + verticalBorder);
    const maxHeight = Math.ceil(lineHeight * 3 + verticalPadding + verticalBorder);

    textarea.style.setProperty("min-height", `${minHeight}px`, "important");
    textarea.style.setProperty("max-height", `${maxHeight}px`, "important");
    textarea.style.setProperty("resize", "none");

    const isEmpty = !(textarea.value || "").trim();
    if (isEmpty) {
      textarea.style.setProperty("height", `${minHeight}px`, "important");
      textarea.style.setProperty("overflow-y", "hidden");
      return;
    }

    textarea.style.setProperty("height", "auto");
    const nextHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight));
    textarea.style.setProperty("height", `${nextHeight}px`, "important");
    textarea.style.setProperty(
      "overflow-y",
      textarea.scrollHeight > maxHeight ? "auto" : "hidden"
    );
  };

  const getCommentNotesTextareas = () =>
    Array.from(
      document.querySelectorAll('textarea[name="commentNotes"]')
    );

  useEffect(() => {
    // If comments are cleared, this option should reset.
    if (!hasCommentNotes) {
      setIncludeOnPrescription(false);
    }
  }, [hasCommentNotes]);

  useLayoutEffect(() => {
    if (!open) return;
    let raf2 = null;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        const textareas = getCommentNotesTextareas();
        textareas.forEach((textarea) => adjustCommentNotesHeight(textarea));
      });
    });

    return () => {
      window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [open, commentNotes]);

  // Helper function to render medication type field (static for MA, dropdown for others)
  const renderMedicationTypeSelect = (props) => {
    const { onChange, label, value, className, isRequired, error, tooltipId, useNonMATooltip = false, useCustomInput = false } = props;
    const tooltipIdValue = tooltipId || "medication-type-tooltip";
    
    // For MA context, show static field
    if (isMAContext) {
      return (
        <div className={`${className} flex flex-col h-full relative`}>
          <div className={`relative rounded-[4px] px-3 py-3 border-[1px] h-[78px] ${
            error ? "border-red-200 border-[2px]" : "border-gray-100"
          } bg-gray-10`}>
            {label && label !== "" && (
              <label className="mb-1 flex items-center gap-2 font-normal text-gray-900 text-xs leading-[18px]">
                <span>
                  {label}{" "}
                  {isRequired && <span className="">*</span>}
                </span>
                <span className="inline-flex cursor-pointer" data-tooltip-id={tooltipIdValue}>
                  <ReactSVG
                    src="/icons/information-dark.svg"
                    className="w-4 h-4 text-teal-300"
                  />
                </span>
              </label>
            )}
            <div className="block w-full text-body-md text-gray-800 text-left">
              Prescribed - Pending Dispense
            </div>
          </div>
          <NewToolTip
            id={tooltipIdValue}
            place="bottom"
            render={() => <MedicationTypeTooltip />}
          />
        </div>
      );
    }

    const medicationValueIsVaccine =
      String(value?.value || "").toLowerCase() === "vaccine";
    if (
      vaccineAnnualReviewLayout ||
      selectedDrugType === "Vaccine"
    ) {
      const fallbackVaccineLabel =
        medicationTypeOptions.find((o) => o.value === "Vaccine")?.label || "Vaccine";
      const displayText =
        medicationValueIsVaccine || value?.value === "Vaccine"
          ? value.label || fallbackVaccineLabel
          : fallbackVaccineLabel;
      const boxBorder = error ? "border-red-200 border-[2px]" : "border-gray-100";
      return (
        <div className={`${className} flex flex-col h-full relative`}>
          <div className={`relative flex h-full min-h-0 flex-col rounded-[4px] px-3 py-3 border-[1px] ${boxBorder} bg-gray-10`}>
            {label ? (
              <label className="mb-1 flex items-center gap-2 font-normal text-gray-900 text-xs leading-[18px]">
                <LabelWithTooltip text={label} tooltipId={tooltipIdValue} required={isRequired} />
              </label>
            ) : null}
            <div className="min-h-0 flex-1 text-left text-body-md text-gray-800">{displayText}</div>
          </div>
          <NewToolTip
            id={tooltipIdValue}
            place="bottom"
            render={() => (useNonMATooltip ? <NonMATooltipContent /> : <MedicationTypeTooltip />)}
          />
        </div>
      );
    }

    // For non-MA contexts, show dropdown without PrescribedRx option
    const filteredOptions = getFilteredMedicationTypeOptions(value, isMAContext);
    
    return (
      <div className={`${className} flex flex-col h-full`}>
        <CommonSelect
          onChange={onChange}
          options={filteredOptions}
          label={label}
          labelSuffix={
            <span data-tooltip-id={tooltipIdValue} className="inline-flex cursor-pointer">
              <ReactSVG
                src="/icons/information-dark.svg"
                className="w-4 h-4 text-teal-300"
              />
            </span>
          }
          value={value}
          className="col-span-12 h-full"
          isRequired={isRequired}
          error={error}
          removeOptional={true}
        />
        <NewToolTip
          id={tooltipIdValue}
          place="bottom"
          render={() => useNonMATooltip ? <NonMATooltipContent /> : <MedicationTypeTooltip />}
        />
      </div>
    );
  };
  const [medicationNameInputMode, setMedicationNameInputMode] = useState(false);
  const [openUncodedDrug, setOpenUncodedDrug] = useState(false);
  const [selectedMedicineError, setSelectedMedicineError] = useState(false);
  const [selectedMedicationTypeError, setSelectedMedicationTypeError] =
    useState(false);
  const [selectedDoseError, setSelectedDoseError] = useState(false);
  const [selectedFormError, setSelectedFormError] = useState(false);
  const [selectedRouteError, setSelectedRouteError] = useState(false);
  const [selectedFrequencyError, setSelectedFrequencyError] = useState(false);
  const [selectedPRNError, setSelectedPRNError] = useState(false);
  const [dispenseQuantityError, setDispenseQuantityError] = useState(false);
  const [ReasonError, setReasonError] = useState(false);
  const [unitError, setUnitError] = useState(false);
  const [dispenseQuantity, setDispenseQuantity] = useState(null);
  const [refills, setRefills] = useState("0");
  const [currentStep, setCurretStep] = useState(1);
  const [brands, setBrands] = useState(null);
  const [addAnotherDrug, setAddAnotherDrug] = useState(false);
  const [showAddMedicationDialog, setShowAddMedicationDialog] = useState(false);
  const [addMedicationInitialData, setAddMedicationInitialData] = useState(null);
  // Persist last saved source locally to ensure it pre-fills on subsequent Add flows
  const [lastSavedSource, setLastSavedSource] = useState(null);
  // Flag to know if Add dialog was opened via Save & Add Another (not a global persist)
  const [openedFromAddAnother, setOpenedFromAddAnother] = useState(false);

  const [vaccineDoseType, setVaccineDoseType] = useState(null);
  const [totalVaccineDoses, setTotalVaccineDoses] = useState(null);
  const [vaccineDoseTypeError, setVaccineDoseTypeError] = useState(false);
  const [totalVaccineDosesError, setTotalVaccineDosesError] = useState(false);
  const isVaccineMedicationType =
    String(medicationType?.value || "").toLowerCase() === "vaccine";
  const isVaccineReasonMode =
    vaccineAnnualReviewLayout ||
    selectedDrugType === "Vaccine" ||
    isVaccineMedicationType;
  const reasonDropdownOptions = isVaccineReasonMode
    ? [
        ...vaccineReasonOptions,
        ...reasonOptions.filter(
          (option) =>
            !vaccineReasonOptions.some(
              (vaccineOption) => vaccineOption.value === option.value
            )
        ),
      ]
    : reasonOptions;

  // Custom validation function for unit field
  const validateUnitField = (unitValue) => {
    // If duration has a value, unit must also have a value
    if (duration && typeof duration === 'string' && duration.trim() !== "") {
      return isValidVigilanceSelection(unitValue);
    }
    // If duration is empty, unit is optional
    return true;
  };

  // Validate dispense quantity which may be an object ({label,value}) or a plain string
  const isValidDispenseQuantity = (dq) => {
    if (!dq) return false;
    if (typeof dq === "string") return dq.trim() !== "";
    const v = (dq?.value || dq?.label || "").toString();
    return v.trim() !== "";
  };

  const fieldsToCheck = [
    {
      field: medicationType,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedMedicationTypeError,
      name: "medicationType",
    },
    {
      field: selectedMedicine,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedMedicineError,
      name: "selectedMedicine",
    },
    {
      field: selectedDose,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedDoseError,
      name: "selectedDose",
    },
    {
      field: selectedForm,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedFormError,
      name: "selectedForm",
    },
    {
      field: selectedRoute,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedRouteError,
      name: "selectedRoute",
    },
    {
      field: selectedFrequency,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedFrequencyError,
      name: "selectedFrequency",
    },
    {
      field: selectedPRN,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setSelectedPRNError,
      name: "selectedPRN",
    },
    {
      field: reason,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setReasonError,
      name: "reason",
    },
    {
      field: dispenseQuantity,
      checkFn: isValidDispenseQuantity,
      setErrorFn: setDispenseQuantityError,
      name: "dispenseQuantity",
    },
    {
      field: unit,
      checkFn: validateUnitField,
      setErrorFn: setUnitError,
      name: "unit",
    },
    {
      field: vaccineDoseType,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setVaccineDoseTypeError,
      name: "vaccineDoseType",
    },
    {
      field: totalVaccineDoses,
      checkFn: isValidVigilanceSelection,
      setErrorFn: setTotalVaccineDosesError,
      name: "totalVaccineDoses",
    },
  ];

  const getFieldsToValidate = () => {
    let fieldsToValidate = fieldsToCheck;
    if (prn) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== "reason"
      );
    }
    if (!prn) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== "selectedPRN"
      );
    }
    // Dispense quantity is required only for MA treatment; keep it optional for Intervention updates (no subType / non-MA).
    if (!subType || !isMAContext) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== "dispenseQuantity"
      );
    }
    if (!isVaccineReasonMode) {
      fieldsToValidate = fieldsToValidate.filter(
        ({ name }) => name !== "vaccineDoseType" && name !== "totalVaccineDoses"
      );
    }
    return fieldsToValidate;
  };

  useEffect(() => {
    if (!open) return;
    
    if (selectedDrugType) {
      const type = medicationTypeOptions.find((x) => x.value === selectedDrugType);
      if (type) setMedicationType(type);
    } else if (isMAContext && create) {
      // For MA context, automatically set to PrescribedRx
      const prescribedRxOption = medicationTypeOptions.find(
        (x) => x.value === drugTypes.PrescribedRx
      );
      if (prescribedRxOption) setMedicationType(prescribedRxOption);
    }
  }, [selectedDrugType, open, isMAContext, create]);

  // Handle initial data from AddMedicationDialog
  useEffect(() => {
    if (open && create && initialData) {
      // Populate fields from initialData when form opens (use empty string instead of null for controlled inputs)
      setStartDate(initialData.startDate || "");
      setCreator(initialData.source || "");
      setCreateCommunicationType(initialData.communicationType || "");
      setPrescriberName(initialData.prescriberName || "");
    } else if (open && create && !initialData) {
      // Reset fields if no initialData when form opens
      setStartDate("");
      setCreator("");
      setCreateCommunicationType("");
      setPrescriberName("");
    }
  }, [initialData, open, create]);

  useEffect(() => {
    setSelectedMedicine({
      ...fullMedication,
      // Ensure the select components receive expected fields
      id: fullMedication?.id || fullMedication?.medicationId || fullMedication?.medicationGenCode || null,
      label:
        fullMedication?.medicationName ||
        fullMedication?.displayText ||
        fullMedication?.label ||
        "",
      value:
        fullMedication?.medicationGenCode ||
        fullMedication?.identifier ||
        fullMedication?.value ||
        "",
      identifier:
        fullMedication?.medicationGenCode ||
        fullMedication?.identifier ||
        fullMedication?.value ||
        "",
      displayText:
        fullMedication?.medicationName ||
        fullMedication?.displayText ||
        fullMedication?.label ||
        "",
      dose: fullMedication?.dosage || fullMedication?.dose || null,
      doseOptions: fullMedication?.doseOptions || (fullMedication?.dosage ? [fullMedication.dosage] : []),
      dispenseQuantity: fullMedication?.dispenseQuantity || null,
    });
    setSelectedDose({
      label: fullMedication?.dosage,
      value: fullMedication?.dosage,
    });
    setSelectedForm({
      label: fullMedication?.form,
      value: fullMedication?.form,
    });
    setSelectedRoute({
      label: fullMedication?.route,
      value: fullMedication?.route,
    });
    setSelectedFrequency({
      label: fullMedication?.frequency,
      value: fullMedication?.frequency,
    });
    setSelectedPRN({
      label: fullMedication?.prnReason,
      value: fullMedication?.prnReason,
    });
    if (fullMedication?.medicationType) {
      setMedicationType({
        label: medicationTypeOptions.find(
          (val) => val.value === fullMedication?.medicationType
        )?.label,
        value: fullMedication?.medicationType,
      });
    }
    setUnit({
      label: unitOptions.find((unit) => unit.value === fullMedication?.unit)
        ?.label,
      value: fullMedication?.unit,
    });
    setDuration(fullMedication?.duration);
    setSig(fullMedication?.sig);
    setIsRx(fullMedication?.isRx);
    setReason({
      label:
        reasonDropdownOptions.find((reason) => reason.value === fullMedication?.reason)
          ?.label || fullMedication?.reason,
      value: fullMedication?.reason,
    });
    setTherapyType(
      getTherapyTypeOption(fullMedication?.therapyType) || null
    );
    // Only set startDate from fullMedication if we're in edit mode or no initialData
    if (!create || !initialData) {
      setStartDate(fullMedication?.startDate);
    }
    // Set prescriberName from fullMedication if editing, or from initialData if creating
    if (!create) {
      setPrescriberName(fullMedication?.prescriberName || "");
    } else if (initialData?.prescriberName) {
      setPrescriberName(initialData.prescriberName);
    }
    setEndDate(fullMedication?.endDate);
    setComments({
      subLabel: specialInstructionOptions.find(
        (reason) => reason.value === fullMedication?.comments
      )?.subLabel,
      label:
        specialInstructionOptions.find(
          (reason) => reason.value === fullMedication?.comments
        )?.label || fullMedication?.comments,
      value: fullMedication?.comments,
    });
    setCustomSpecialInstruction("");
    setCommentNotes(fullMedication?.commentNotes || "");
    setIncludeOnPrescription(Boolean(fullMedication?.includeOnPrescription));
    setDispenseQuantity({
      label:
        dispenseOptions.find(
          (dispense) => dispense.value === fullMedication?.dispenseQuantity
        )?.label || fullMedication?.dispenseQuantity,
      value: fullMedication?.dispenseQuantity,
    });
    setRefills(fullMedication?.refills ?? "0");
    if (fullMedication?.vaccineDoseType != null && fullMedication?.vaccineDoseType !== "") {
      const v = fullMedication.vaccineDoseType;
      setVaccineDoseType(
        typeof v === "object" && v !== null
          ? v
          : { label: String(v), value: v }
      );
    } else {
      setVaccineDoseType(null);
    }
    if (
      fullMedication?.totalNumberOfVaccineDoses != null &&
      fullMedication?.totalNumberOfVaccineDoses !== ""
    ) {
      const v = fullMedication.totalNumberOfVaccineDoses;
      setTotalVaccineDoses(
        typeof v === "object" && v !== null
          ? v
          : { label: String(v), value: v }
      );
    } else {
      setTotalVaccineDoses(null);
    }
  }, [fullMedication, create, initialData]);

  const resetForm = () => {
    setClicked(false);
    setRecentStart(null);
    setSelectedMedicine(null);
    setSelectedDose(null);
    setSelectedForm(null);
    setSelectedRoute(null);
    setSelectedFrequency(null);
    setSelectedPRN(null);
    setUnit(null);
    setDuration(null);
    setSig("");
    setIsRx(false);
    if (isVaccineReasonMode) {
      setReason({ label: "vaccine schedule", value: "vaccine schedule" });
    } else {
      setReason(null);
    }
    setTherapyType(null);
    setCustomReason("");
    setComments("");
    setCustomSpecialInstruction("");
    setCommentNotes("");
    setIncludeOnPrescription(false);
    setStartDate("");
    setEndDate("");
    setPrescriberName("");
    if (vaccineAnnualReviewLayout && selectedDrugType) {
      const match = medicationTypeOptions.find((x) => x.value === selectedDrugType);
      setMedicationType(match || null);
    } else {
      setMedicationType(null);
    }
    setRefills("0");
    setCurretStep(1);
    setVaccineDoseType(null);
    setTotalVaccineDoses(null);
    setVaccineDoseTypeError(false);
    setTotalVaccineDosesError(false);
  };

  useEffect(() => {
    if (prn) {
      setReason(null);
    } else {
      setSelectedPRN(null);
    }
  }, [prn]);

  useEffect(() => {
    if (!open || !isVaccineReasonMode || prn) return;
    if (reason?.value) return;
    setReason({ label: "vaccine schedule", value: "vaccine schedule" });
  }, [open, isVaccineReasonMode, prn, reason?.value]);

  useEffect(() => {
    if (isVaccineMedicationType) {
      setTherapyType(null);
    }
  }, [isVaccineMedicationType]);

  const manageStep = (addAnother) => {
    setClicked(true);
    let allFieldsValid = true;
    const fieldsToValidate = getFieldsToValidate();
    fieldsToValidate.forEach(({ field, checkFn, setErrorFn }) => {
      const hasError = !checkFn(field);
      setErrorFn(hasError);
      if (hasError) {
        allFieldsValid = false;
      }
    });
    if (allFieldsValid) {
      const resolvedMedicationId =
        fullMedication?.id ||
        selectedMedicine?.id ||
        selectedMedicine?.medicationId ||
        null;
      const resolvedMedicationName =
        selectedMedicine?.displayText ||
        selectedMedicine?.medicationName ||
        fullMedication?.medicationName ||
        "";
      if (resolvedMedicationId || resolvedMedicationName) {
        try {
          savePrescriptionNotesOverride({
            medicationId: resolvedMedicationId,
            medicationName: resolvedMedicationName,
            includeOnPrescription,
            commentNotes,
          });
        } catch (e) {
          // best-effort client-side preview data only
        }
      }

      if (create) {
        let medication = {
          medications: [
            {
              recentStartIndicator: recentStart || false,
              medicationName: selectedMedicine.displayText,
              medicationType: medicationType?.value,
              medicationGenCode: selectedMedicine.identifier,
              medicationGenCodeType: selectedMedicine.type,
              dosage: selectedDose.value,
              form: selectedForm.value,
              frequency: selectedFrequency.value,
              route: selectedRoute.value,
              prn: prn,
              prnReason: selectedPRN?.value,
              reason: isOtherReasonSelected ? (customReason.trim() || "Other") : reason?.value,
              therapyType: therapyType?.value || null,
              duration: duration,
              unit: unit?.value || null,
              comments: resolvedSpecialInstruction,
              commentNotes: commentNotes?.trim() || null,
              includeOnPrescription: includeOnPrescription,
              startDate: startDate,
              endDate: endDate,
              status: status,
              created: created,
              dispenseQuantity: dispenseQuantity?.value || dispenseQuantity?.label || null,
              refills: refills,
              sig: sig,
              isRx: subType !== null || isRx,
              manufacturerStoredType: selectedMedicine?.manufacturerStoredType,
              manufacturerCode: selectedMedicine?.manufacturerCode,
              manufacturerName: selectedMedicine?.manufacturerName,
              creator: creator,
              createCommunicationType: createCommunicationType,
              prescriberName: prescriberName?.trim() || null,
              ...(isVaccineReasonMode
                ? {
                    vaccineDoseType:
                      vaccineDoseType?.value ?? vaccineDoseType?.label ?? null,
                    totalNumberOfVaccineDoses:
                      totalVaccineDoses?.value ??
                      totalVaccineDoses?.label ??
                      null,
                  }
                : {}),
            },
          ],
        };
        if (customSubmit) {
          customSubmitAction(medication, subType);
        } else {
          dispatch(createMedication(patientId, medication));
        }
      } else {
        let medication = {
          medications: [
            {
              id: fullMedication.id,
              recentStartIndicator: recentStart || false,
              medicationType: medicationType?.value,
              medicationName:
                selectedMedicine?.displayText || fullMedication.medicationName,
              dosage: selectedDose.value,
              form: selectedForm.value,
              frequency: selectedFrequency.value,
              route: selectedRoute.value,
              prn: prn,
              prnReason: selectedPRN?.value,
              reason: isOtherReasonSelected ? (customReason.trim() || "Other") : reason?.value,
              therapyType: therapyType?.value || null,
              duration: duration,
              unit: unit?.value || null,
              comments: resolvedSpecialInstruction,
              commentNotes: commentNotes?.trim() || null,
              includeOnPrescription: includeOnPrescription,
              startDate: startDate,
              endDate: endDate,
              status: status,
              created: created,
              dispenseQuantity: dispenseQuantity?.value || dispenseQuantity?.label || null,
              refills: refills,
              medicationGenCode:
                selectedMedicine?.identifier ||
                selectedMedicine?.value ||
                fullMedication.medicationGenCode,
              medicationGenCodeType:
                selectedMedicine.type || fullMedication.medicationGenCodeType,
              sig: sig,
              isRx: subType !== null || isRx,
              manufacturerStoredType: selectedMedicine?.manufacturerStoredType,
              manufacturerCode: selectedMedicine?.manufacturerCode,
              manufacturerName: selectedMedicine?.manufacturerName,
              creator: creator,
              createCommunicationType: createCommunicationType,
              prescriberName: prescriberName?.trim() || null,
              ...(isVaccineReasonMode
                ? {
                    vaccineDoseType:
                      vaccineDoseType?.value ?? vaccineDoseType?.label ?? null,
                    totalNumberOfVaccineDoses:
                      totalVaccineDoses?.value ??
                      totalVaccineDoses?.label ??
                      null,
                  }
                : {}),
            },
          ],
        };
        if (customSubmit) {
          customSubmitAction(medication, subType);
        } else {
          dispatch(updateMedication(patientId, medication));
        }
      }

      if (addAnother) {
        if (!vaccineAnnualReviewLayout) {
          // Open AddMedicationDialog with current form values (keep MedicationCreate open)
          const payload = {
            startDate: startDate || new Date().toISOString().split('T')[0],
            // Prefer lastSavedSource, then persisted addMedicationInitialData, then creator
            source: lastSavedSource || addMedicationInitialData?.source || creator || null,
            communicationType: createCommunicationType || addMedicationInitialData?.communicationType || null,
            prescriberName: prescriberName?.trim() || "",
            applyToAll: addMedicationInitialData?.applyToAll || false,
            // Mark that this payload was created from Save & Add Another flow
            __openedFromAddAnother: true,
          };
          setAddMedicationInitialData(payload);
          setOpenedFromAddAnother(true);
          onSave(selectedMedicine?.displayText || fullMedication.medicationName, {
            saveAndAddAnother: true,
          });
          resetForm();
          setShowAddMedicationDialog(true);
        } else {
          // AMR vaccine: stay in slider, fresh form, no AddMedicationDialog (Figma)
          onSave(selectedMedicine?.displayText || fullMedication.medicationName, {
            saveAndAddAnother: true,
          });
          resetForm();
        }
      } else {
        onSave(selectedMedicine?.displayText || fullMedication.medicationName, {
          saveAndAddAnother: false,
        });
        resetForm();
        if (closeOnSave !== false) {
          onClose(false);
        }
      }
    }
  };

  useEffect(() => {
    if (clicked) {
      const fieldsToValidate = getFieldsToValidate();
      const activeFieldNames = new Set(fieldsToValidate.map(({ name }) => name));
      fieldsToCheck.forEach(({ field, checkFn, setErrorFn, name }) => {
        if (!activeFieldNames.has(name)) {
          // Clear stale errors for fields not required in current flow.
          setErrorFn(false);
          return;
        }
        const hasError = !checkFn(field);
        setErrorFn(hasError);
      });
    }
  }, [clicked, duration, ...fieldsToCheck.map(({ field }) => field)]);

  useEffect(() => {
    if (selectedMedicine) {
      if (selectedMedicine?.sig) {
        setSig(selectedMedicine?.sig);
      }
      if (selectedMedicine?.isRx) {
        setIsRx(selectedMedicine?.isRx);
      }
      if (selectedMedicine?.dose) {
        setSelectedDose({
          label: selectedMedicine?.dose,
          value: selectedMedicine?.dose,
        });
      }
      if (
        selectedMedicine?.dispenseQuantity &&
        (selectedMedicine?.id ||
          [
            "firstLineOralAntihistamines",
            "firstLineIntranasalCorticosteroids",
            "stepUpIntranasalCorticosteroids",
            "stepUpCombination",
          ].includes(subType))
      ) {
        setDispenseQuantity({
          label:
            dispenseOptions.find(
              (dispense) =>
                dispense.value === selectedMedicine?.dispenseQuantity
            )?.label || selectedMedicine?.dispenseQuantity,
          value: selectedMedicine?.dispenseQuantity,
        });
      }
      if (selectedMedicine?.form) {
        if (
          (selectedMedicine?.form?.includes("syr") ||
            selectedMedicine?.form?.includes("liq")) &&
          !selectedMedicine?.dispenseQuantity &&
          !selectedMedicine?.id
        )
          setDispenseQuantity({
            label: "1 Bottle",
            value: "1 Bottle",
          });
        setSelectedForm({
          label: selectedMedicine?.form,
          value: selectedMedicine?.form,
        });
      }
      if (selectedMedicine?.route) {
        setSelectedRoute({
          label: selectedMedicine?.route,
          value: selectedMedicine?.route,
        });
      }
      if (selectedMedicine?.frequency) {
        setSelectedFrequency({
          label: selectedMedicine?.frequency,
          value: selectedMedicine?.frequency,
        });
      }
      if (selectedMedicine?.reason) {
        setReason({
          label:
            reasonDropdownOptions.find(
              (reason) => reason.value === selectedMedicine?.reason
            )?.label || selectedMedicine?.reason,
          value: selectedMedicine?.reason,
        });
      }
      if (selectedMedicine?.duration) {
        setDuration(selectedMedicine?.duration);
      }
      if (selectedMedicine?.unit) {
        setUnit({
          label: unitOptions.find(
            (unit) => unit.value === selectedMedicine?.unit
          )?.label,
          value: selectedMedicine?.unit,
        });
      }
    } else {
      setSig("");
      setIsRx(isRxService);
      setSelectedDose(null);
      setSelectedForm(null);
      setSelectedRoute(null);
      setDispenseQuantity(null);
      setSelectedFrequency(null);
    }
  }, [selectedMedicine, open]);

  const isDurationProvided = (() => {
    if (duration === null || typeof duration === "undefined") return false;
    if (typeof duration === "number") return duration > 0;
    const s = String(duration).trim();
    return s !== "" && s !== "0";
  })();

  useEffect(() => {
    if (!isDurationProvided) {
      setUnitError(false);
    }
  }, [isDurationProvided]);

  useEffect(() => {
    setTimeout(() => {
      if (!selectedMedicine?.id && subType && !informationStep) {
        setSelectedDose({
          label: "Use as Directed",
          value: "Use as Directed",
        });
        setSelectedForm({
          label: "Rinse",
          value: "Rinse",
        });
        setSelectedRoute({
          label: "Intranasal",
          value: "Intranasal",
        });
        setSelectedFrequency({
          label: "As Directed",
          value: "As Directed",
        });
        setReason({
          label: "allergic rhinitis",
          value: "allergic_rhinitis",
        });
        if (nonPharmacologicalTypes.includes(subType))
          setDispenseQuantity({
            label: "1",
            value: "1",
          });
      }
    }, 300);
  }, [selectedMedicine, subType, informationStep, open]);

  const renderSubTypeRadioAndSelect = () => {
    switch (subType) {
      case "nasalSalineRinses":
        return (
          <>
            <p className="text-body-sm col-span-12 text-gray-900 font-semibold">
              Brand Names:{" "}
              <span className="font-normal">
                NeilMed NasaFlo NetiPot, NeilMed NasaFlo Porcelain, NeilMed
                Sinus Rinse, HydraSense NetiRinse.
              </span>
            </p>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "nasal-saline-rinses-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
            />
          </>
        );
      case "nasalSalineSprays":
        return (
          <>
            <p className="text-body-sm col-span-12 text-gray-900 font-semibold">
              Brand Names:{" "}
              <span className="font-normal">
                HydraSense products (nasal spray, mist), Otrivin products
                (solution, nasal spray, drops), Reactine Naturease Nasal Spray,
                Rhinaris products (mist, nasal spray, drops), Salinex products
                (solution, mist, nasal spray), Simply Saline Adult Nasal Mist.
              </span>
            </p>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "nasal-saline-sprays-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
            />
          </>
        );
      case "nasalLubricants":
        return (
          <>
            <p className="text-body-sm col-span-12 text-gray-900 font-semibold">
              Brand Names:{" "}
              <span className="font-normal">
                Rhinaris® Lubricating Nasal Gel, Salinex® Nasal Lubricant Gel,
                Secaris® gel
              </span>
            </p>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "nasal-lubricants-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
            />
          </>
        );
      case "firstLineOralAntihistamines":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "first-line-oral-antihistamines-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
              customEndpoint="/drugs/oralAntihistamines"
              strength={selectedMedicineStrength}
              viewCoverage={true}
            />
          </>
        );
      case "addOnTherapyOralAntihistamines":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "add-on-therapy-oral-antihistamines-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
              customEndpoint="/drugs/oralAntihistamines"
              strength={selectedMedicineStrength}
              viewCoverage={true}
            />
          </>
        );
      case "firstLineIntranasalCorticosteroids":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "first-line-intranasal-corticosteroids-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
              customEndpoint="/drugs/nasalCorticosteroids"
              strength={selectedMedicineStrength}
              viewCoverage={true}
            />
          </>
        );
      case "stepUpIntranasalCorticosteroids":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "step-up-intranasal-corticosteroids-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
              customEndpoint="/drugs/nasalCorticosteroids"
              strength={selectedMedicineStrength}
              viewCoverage={true}
            />
          </>
        );
      case "alternativeFirstLineCombination":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "alternative-first-line-combination-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
              customEndpoint="/drugs/antihistamneCorticosteroidCombination"
              strength={selectedMedicineStrength}
              viewCoverage={true}
            />
          </>
        );
      case "stepUpCombination":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "step-up-combination-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
              customEndpoint="/drugs/antihistamneCorticosteroidCombination"
              strength={selectedMedicineStrength}
              viewCoverage={true}
            />
          </>
        );
      case "addOnTherapyOralDecongestants":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "add-on-therapy-oral-decongestants-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
            />
          </>
        );
      case "addOnTherapyIntranasalDecongestants":
        return (
          <>
            <div className="flex items-center justify-between col-span-12">
              <button
                onClick={() => setCurretStep(1)}
                className="flex items-center space-x-1"
              >
                <img src="/icons/arrow-left.svg" className="w-4 h-4" />
                <span className="text-gray-800 text-[14px] font-medium">
                  Back
                </span>
              </button>
              <p className="text-gray-800 text-[14px] font-medium">2 of 2</p>
            </div>
            {brands && (
              <p className="text-body-sm col-span-12 text-gray-900 font-semibold">
                Brand Names:{" "}
                <span className="font-normal">
                  {brands.replace("Brand Names: ", "")}
                </span>
              </p>
            )}
            {renderMedicationTypeSelect({
              onChange: (value) => setMedicationType(value),
              options: medicationTypeOptions,
              label: "Medication Type",
              value: medicationType,
              className: "col-span-12",
              isRequired: true,
              error: selectedMedicationTypeError,
              tooltipId: "add-on-therapy-intranasal-decongestants-tooltip"
            })}
            <MedicationSelect
              onChange={(medicine) => setSelectedMedicine(medicine)}
              isRequired={true}
              onNewCreate={(value) => setMedicationNameInputMode(value)}
              error={selectedMedicineError}
              value={selectedMedicine}
            />
          </>
        );
      default:
        break;
    }
  };

  // Get singular or plural form type based on quantity
  const getFormType = (formType, quantity) => {
    if (!formType) return "";
    if (parseInt(quantity) === 1) return formType.toLowerCase().trim();
    
    const formLower = formType.toLowerCase().trim();
    const pluralMap = {
      tablet: "tablets", capsule: "capsules", syringe: "syringes", bottle: "bottles",
      vial: "vials", patch: "patches", suppository: "suppositories", lozenge: "lozenges",
      spray: "sprays", drop: "drops", puff: "puffs", inhalation: "inhalations",
      unit: "units", pack: "packs", box: "boxes", tube: "tubes", sachet: "sachets", rinse: "rinses"
    };
    if (pluralMap[formLower]) return pluralMap[formLower];
    if (formLower.match(/[sxz]$/)) return formType;
    if (formLower.endsWith("y")) return formType.slice(0, -1) + "ies";
    return formType + "s";
  };

  // Check if value exists in predefined options
  const isFromPredefinedOptions = (value) => {
    if (!value?.value) return false;
    const isTablet = selectedMedicine?.form?.includes("tablet");
    const isLiquid = selectedMedicine?.form?.includes("syr") || selectedMedicine?.form?.includes("liq");
    return (isTablet && dispenseTabletOptions.some(opt => opt.value === value.value)) ||
           (isLiquid && dispenseOptions.some(opt => opt.value === value.value));
  };

  // Check if form type is already present in quantity value
  const hasFormType = (quantityValue, formType) => {
    const quantityLower = quantityValue.toLowerCase();
    const formLower = formType.toLowerCase();
    const pluralForm = getFormType(formType, "2").toLowerCase();
    return quantityLower.includes(formLower) || quantityLower.includes(pluralForm);
  };

  // Append form type to quantity value if needed
  const appendFormTypeIfNeeded = (value) => {
    if (!value || !selectedForm?.value) return value;
    if (isFromPredefinedOptions(value)) return value;

    const quantityValue = value.value || value.label || "";
    const numberMatch = quantityValue.match(/^\d+/);
    if (!numberMatch) return value;

    const number = numberMatch[0];
    if (hasFormType(quantityValue, selectedForm.value)) return value;

    const formTypeText = getFormType(selectedForm.value, number);
    return { label: `${number} ${formTypeText}`, value: `${number} ${formTypeText}` };
  };

  // Handle dispense quantity change
  const handleDispenseQuantityChange = (value) => {
    const updated = appendFormTypeIfNeeded(value);
    // Normalize to object with label and value strings when possible
    let normalized = updated;
    if (!updated) {
      normalized = null;
    } else if (typeof updated === "string") {
      const s = updated.trim();
      normalized = { label: s, value: s };
    } else if (typeof updated === "object") {
      const label = (updated.label || updated.value || "").toString();
      const valueStr = (updated.value || updated.label || "").toString();
      normalized = { ...updated, label: label, value: valueStr };
    }
    setDispenseQuantity(normalized);
  };

  const handleClearDuration = () => {
    setDuration("");
    setUnit(null);
    setStartDate(null);
    setEndDate(null);
    setUnitError(false);
  };

  // Append form type to existing dispense quantity when form is selected
  useEffect(() => {
    if (!dispenseQuantity || !selectedForm?.value) return;
    const updatedValue = appendFormTypeIfNeeded(dispenseQuantity);
    if (updatedValue !== dispenseQuantity && updatedValue?.value !== dispenseQuantity?.value) {
      setDispenseQuantity(updatedValue);
    }
  }, [selectedForm?.value]);

  const renderSubTypeCustomFieldsBottom = () => {
    switch (subType) {
      case "nasalSalineRinses":
        return (
          <>
            <CommonSelect
              onChange={handleDispenseQuantityChange}
              isCreatable={true}
              options={
                selectedMedicine?.form?.includes("tablet")
                  ? dispenseTabletOptions
                  : selectedMedicine?.form?.includes("syr") ||
                    selectedMedicine?.form?.includes("liq")
                  ? dispenseOptions
                  : []
              }
              label="Dispense Quantity"
              value={dispenseQuantity}
              className="col-span-6 "
              isRequired={isMAContext}
              error={dispenseQuantityError}
            />
            <CustomInput
              name="refills"
              className="col-span-6"
              label="Refills"
              placeholder="Refills"
              value={refills}
              onChange={(e) => setRefills(e.target.value)}
              type="text"
              showOptionalLabel={false}
            />
          </>
        );
      case "nasalSalineSprays":
        return (
          <>
            <CommonSelect
              onChange={handleDispenseQuantityChange}
              isCreatable={true}
              options={
                selectedMedicine?.form?.includes("tablet")
                  ? dispenseTabletOptions
                  : selectedMedicine?.form?.includes("syr") ||
                    selectedMedicine?.form?.includes("liq")
                  ? dispenseOptions
                  : []
              }
              label="Dispense Quantity"
              value={dispenseQuantity}
              className="col-span-6 "
              isRequired={isMAContext}
              error={dispenseQuantityError}
            />
            <CustomInput
              name="refills"
              className="col-span-6"
              label="Refills"
              placeholder="Refills"
              value={refills}
              onChange={(e) => setRefills(e.target.value)}
              type="text"
              showOptionalLabel={false}
            />
          </>
        );
      case "nasalLubricants":
        return (
          <>
            <CommonSelect
              onChange={handleDispenseQuantityChange}
              isCreatable={true}
              options={
                selectedMedicine?.form?.includes("tablet")
                  ? dispenseTabletOptions
                  : selectedMedicine?.form?.includes("syr") ||
                    selectedMedicine?.form?.includes("liq")
                  ? dispenseOptions
                  : []
              }
              label="Dispense Quantity"
              value={dispenseQuantity}
              className="col-span-6 "
              isRequired={isMAContext}
              error={dispenseQuantityError}
            />
            <CustomInput
              name="refills"
              className="col-span-6"
              label="Refills"
              placeholder="Refills"
              value={refills}
              onChange={(e) => setRefills(e.target.value)}
              type="text"
              showOptionalLabel={false}
            />
          </>
        );
      case subType:
        return (
          <>
            <CommonSelect
              onChange={handleDispenseQuantityChange}
              isCreatable={true}
              options={
                selectedMedicine?.form?.includes("tablet")
                  ? dispenseTabletOptions
                  : selectedMedicine?.form?.includes("syr") ||
                    selectedMedicine?.form?.includes("liq")
                  ? dispenseOptions
                  : []
              }
              label="Dispense Quantity"
              value={dispenseQuantity}
              className="col-span-6 "
              isRequired={isMAContext}
              error={dispenseQuantityError}
            />
            <CustomInput
              name="refills"
              className="col-span-6"
              label="Refills"
              placeholder="Refills"
              value={refills}
              onChange={(e) => setRefills(e.target.value)}
              type="text"
              showOptionalLabel={false}
            />
          </>
        );
      default:
        break;
    }
  };

  useEffect(() => {
    if (create) {
      setCurretStep(1);
    } else {
      setCurretStep(2);
    }
  }, [create, open]);

  useEffect(() => {
    setAddMedicationInitialData(medicationInitialData || null);
    if (medicationInitialData?.source) {
      setLastSavedSource(medicationInitialData.source);
    }
  }, [medicationInitialData]);

  const handleAddMedicationSave = (data) => {
    setShowAddMedicationDialog(false);

    if (!data) {
      setOpenedFromAddAnother(false);
      if (onSaveAndAddAnother) {
        onSaveAndAddAnother(data);
      } else {
        onClose(true);
      }
      return;
    }

    const payload = { ...data };
    if (payload.__openedFromAddAnother) delete payload.__openedFromAddAnother;

    // Persist to Redux only when user checked 'applyToAll' (explicit persist)
    if (payload.applyToAll) {
      setAddMedicationInitialData(payload);
      dispatch(setMedicationInitialData(payload));
      if (payload.source) setLastSavedSource(payload.source);
    } else if (openedFromAddAnother) {
      // Only update lastSavedSource locally for Save & Add Another flow; do NOT persist to Redux
      if (payload.source) setLastSavedSource(payload.source);
      // Clear transient local initial data to avoid affecting fresh popups
      setAddMedicationInitialData(null);
    }

    // reset the flag
    setOpenedFromAddAnother(false);

    if (onSaveAndAddAnother) {
      onSaveAndAddAnother(data);
    } else {
      onClose(true);
    }
  };

  return (
    <>
      {showAddMedicationDialog && createPortal(
        <AddMedicationDialog
          open={showAddMedicationDialog}
          onClose={() => {
            setShowAddMedicationDialog(false);
            // Close MedicationCreate when dialog is closed
            onClose(false);
          }}
          onSave={handleAddMedicationSave}
          patientId={patientId}
          initialData={addMedicationInitialData}
        />,
        document.body
      )}
      <CommonSlider
        open={open}
        onClose={() => {
          onClose(false);
          setCurretStep(1);
        }}
      >
      <div
        className={`flex flex-1 min-w-0 min-h-0 flex-col h-full ${
          vaccineAnnualReviewLayout ? "bg-gray-10" : ""
        }`}
      >
      {informationStep && currentStep === 1 ? (
        <InformationStep
          activeButton={activeButton}
          setActiveButton={setActiveButton}
          onNext={() => {
            setCurretStep(2);
            if (create) {
              setSelectedMedicine(null);
              setSelectedFrequency(null);
              setSelectedDose(null);
              setSelectedForm(null);
              setSelectedRoute(null);
              setReason(null);
              setSig("");
              setDispenseQuantity(null);
            }
          }}
          onBack={() => setCurretStep(1)}
          create={create}
          currentStep={currentStep}
          type={subType}
          onClose={(value) => {
            onClose(value);
            setCurretStep(1);
          }}
          onMedicineClick={(value, brandNames = null) => {
            setSelectedMedicine(value);
            setBrands(brandNames);
            setCurretStep(2);
          }}
        />
      ) : (
        <div ref={scrollContainerRef} className="flex flex-col flex-1 h-full overflow-y-auto">
          <div className="relative flex flex-col flex-1">
            {/* Header */}
            <div className={`px-8 py-6 ${ vaccineAnnualReviewLayout ? "bg-gray-10" : "bg-gray-20"}`}>
              <div className="flex items-start justify-between space-x-3">
                <div className="flex space-x-1">
                  <h2 className="font-bold text-gray-900 text-display-xs">
                    {(vaccineAnnualReviewLayout ? "Update" : create ? "Add" : "Update")}{" "}
                    {title && title.length > 0
                      ? title
                      : `Medication${
                          medicationName ? ` - ${medicationName}` : ""
                        }`}
                  </h2>
                </div>
                <div className="flex items-center h-7">
                  <button
                    type="button"
                    className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer right-4 top-3 bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                    onClick={() => {
                      onClose(false);
                      setCurretStep(1);
                    }}
                  >
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>

            {step != null && maxSteps > 1 && (
              <div className="text-sm flex justify-end pr-9 pt-4">
                {step} of {maxSteps}
              </div>
            )}
            <div
              ref={formBodyRef}
              className="flex flex-col justify-between flex-1 px-8 pb-6 pt-4 h-full overflow-y-auto"
              id="form-body"
            >
              <div
                className={`grid grid-cols-12 gap-3 ${
                  vaccineAnnualReviewLayout ? "" : "h-[500px]"
                }`}
              >
                {medicationName !== null && !subType && !vaccineAnnualReviewLayout && (
                  <div className="col-span-12">
                        <CustomInput
                          options={recentStartOptions}
                          name="ConfirmRecentStart"
                          className="bg-gray-10 !ring-yellow-200"
                          label={<LabelWithTooltip text="Confirm Recent Start" tooltipId="confirm-recent-start-tooltip" required={true} />}
                          type="dropdown"
                          value={recentStart}
                          onChange={(e) => setRecentStart(e.value)}
                          required
                          showInfo={false}
                        />
                    <NewToolTip id="confirm-recent-start-tooltip" render={() => <RecentStartToolTip />} place="right" />
                  </div>
                )}
                {renderSubTypeRadioAndSelect()}
                {subType ? null : (
                  <MedicationSelect
                    onChange={(medicine) => setSelectedMedicine(medicine)}
                    isRequired={true}
                    onNewCreate={(value) => setMedicationNameInputMode(value)}
                    error={selectedMedicineError}
                    value={selectedMedicine}
                    isDisabled={!create && !isIntervention}
                    showDin={true}
                    topSlot={renderMedicationTypeSelect({
                      onChange: (value) => setMedicationType(value),
                      label: "Medication Type",
                      value: medicationType,
                      className: "col-span-12 h-full",
                      isRequired: true,
                      error: selectedMedicationTypeError,
                      useNonMATooltip: true,
                      useCustomInput: true
                    })}
                    searchFilterRequired={vaccineAnnualReviewLayout}
                  />
                )}
                {medicationNameInputMode && selectedMedicine?.value && (
                  <p className="col-span-12 text-body-sm text-red-200 font-medium">
                    This is an UNCODED DRUG - No Clinical Check will be
                    performed
                  </p>
                )}
                <DosageSelect
                  selectedMedicine={selectedMedicine || initialMedicine}
                  onChange={(dose) => {
                    setSelectedDose(dose);
                  }}
                  isRequired={true}
                  error={selectedDoseError}
                  value={selectedDose}
                  subType={subType}
                  isDisabled={
                    !!subType ||
                    (selectedDose &&
                      (selectedMedicine?.type === "Din" ||
                        initialMedicine?.type === "Din" ||
                        selectedMedicine?.type === "Sentence" ||
                        initialMedicine?.type === "Sentence")) ||
                    (!create && !isIntervention)
                  }
                />
                <FormSelect
                  selectedMedicine={selectedMedicine || initialMedicine}
                  onChange={(form) => setSelectedForm(form)}
                  isRequired={true}
                  error={selectedFormError}
                  value={selectedForm}
                  isDisabled={
                    !!subType ||
                    (selectedForm &&
                      (selectedMedicine?.type === "Din" ||
                        initialMedicine?.type === "Din" ||
                        selectedMedicine?.type === "Sentence" ||
                        initialMedicine?.type === "Sentence")) ||
                    (!create && !isIntervention)
                  }
                />
                <RouteSelect
                  selectedMedicine={selectedMedicine || initialMedicine}
                  onChange={(route) => {
                    setSelectedRoute(route);
                    if (route) setSelectedRouteError(false);
                  }}
                  isRequired={true}
                  error={selectedRouteError}
                  value={selectedRoute}
                  isDisabled={
                    !!subType ||
                    (selectedRoute &&
                      (selectedMedicine?.type === "Din" ||
                        initialMedicine?.type === "Din" ||
                        selectedMedicine?.type === "Sentence" ||
                        initialMedicine?.type === "Sentence")) ||
                    (!create && !isIntervention)
                  }
                />
                {isVaccineReasonMode && (
                  <>
                    <CommonSelect
                      onChange={(value) => setVaccineDoseType(value)}
                      isCreatable={true}
                      options={vaccineDoseTypeOptions}
                      label="Vaccine Dose Type"
                      value={vaccineDoseType}
                      className="col-span-6"
                      isRequired={true}
                      error={vaccineDoseTypeError}
                    />
                    <CommonSelect
                      onChange={(value) => setTotalVaccineDoses(value)}
                      isCreatable={false}
                      options={totalVaccineDoseOptions}
                      label=" Vaccine Doses"
                      value={totalVaccineDoses}
                      className="col-span-6"
                      isRequired={true}
                      error={totalVaccineDosesError}
                    />
                  </>
                )}
                <FrequencySelect
                  onChange={(frequency) => {
                    setSelectedFrequency(frequency);
                    if (PRN_FREQUENCY_VALUES.includes(frequency?.value)) {
                      setPrn(true);
                    }
                    if (frequency?.value !== selectedFrequency?.value)
                      scrollY("form-body");
                  }}
                  isRequired={true}
                  subType={subType}
                  error={selectedFrequencyError}
                  value={selectedFrequency}
                  selectedMedicine={selectedMedicine || initialMedicine}
                  isDisabled={!!subType || (!create && !isIntervention)}
                />
                <Button
                  type={"primary-outline"}
                  text="Customize"
                  className="col-span-4 !px-2.5 !py-3.5"
                  disabled={true}
                />
                <CustomInput
                  name="sig"
                  className="col-span-12"
                  label={isVaccineReasonMode ? "SIG (Optional)" : "SIG"}
                  placeholder="SIG"
                  value={sig}
                  onChange={(e) => setSig(e.target.value)}
                  type="text"
                  showOptionalLabel={!isVaccineReasonMode}
                />
                <div className="p-1 col-span-12"></div>
                <Button
                  type={
                    selectedMedicine?.prn || prn ? "primary" : "primary-outline"
                  }
                  text="PRN"
                  onClick={() => setPrn(!prn)}
                  className="col-span-4 !px-2.5 !py-3.5"
                />
                <PRNSelect
                  selectedMedicine={selectedMedicine || initialMedicine}
                  onChange={(prn) => setSelectedPRN(prn)}
                  isRequired={true}
                  isDisabled={!prn}
                  error={selectedPRNError}
                  value={selectedPRN}
                />
                <div className="col-span-12 grid grid-cols-12 gap-3">
                  <div
                    className={
                      isVaccineMedicationType ? "col-span-12" : "col-span-8"
                    }
                  >
                    <MedicalConditionSelect
                      onChange={(value) => {
                        setReason(value);
                        setCustomReason("");
                      }}
                      isCreatable={true}
                      defaultOptions={reasonDropdownOptions}
                      label="Reason"
                      error={ReasonError}
                      isRequired={true}
                      value={reason}
                      disabled={prn}
                      useMenuPortal={true}
                    />
                  </div>
                  {!isVaccineMedicationType && (
                    <div className="col-span-4">
                      <MedicalConditionSelect
                        onChange={(value) => setTherapyType(value)}
                        isCreatable={false}
                        defaultOptions={therapyTypeOptions}
                        label="Therapy Type"
                        value={therapyType}
                        disabled={prn}
                        useMenuPortal={true}
                        isRequired
                      />
                    </div>
                  )}
                </div>
                {isOtherReasonSelected && (
                  <CustomInput
                    name="customReason"
                    className="col-span-12"
                    label="Specify Reason"
                    placeholder="Enter reason"
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    type="text"
                    showOptionalLabel={true}
                  />
                )}

                {!vaccineAnnualReviewLayout && (
                <div className="col-span-12 mt-2 rounded-[4px] bg-white">
                  <div className="grid grid-cols-12 gap-3">
                    <CustomInput
                      value={duration}
                      onChange={(e) => {
                        const nextDuration = e.target.value;
                        setDuration(nextDuration);
                        if (String(nextDuration || "").trim() === "") {
                          setUnitError(false);
                        }
                      }}
                      name="Duration"
                      className="col-span-4"
                      label="Duration"
                      type="age"
                      showInputCancellation={isDurationProvided}
                      onClose={handleClearDuration}
                    />
                    <CommonSelect
                      onChange={(value) => {
                        setUnit(value);
                        // If duration is empty, unit is optional and should not remain in error state.
                        if (!isDurationProvided) {
                          setUnitError(false);
                        }
                      }}
                      isCreatable={true}
                      options={unitOptions}
                      label="Duration Unit"
                      className="col-span-8"
                      value={unit}
                      error={unitError}
                      isRequired={duration && typeof duration === 'string' && duration.trim() !== ""}
                      disabled={!isDurationProvided}
                    />
                    <CustomInput
                      name="Start Date"
                      className="col-span-6 mb-4"
                      label="Start Date"
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        setStartDate(e);
                      }}
                    />
                    <CustomInput
                      name="End Date"
                      className="col-span-6 mb-4"
                      label="End Date"
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        setEndDate(e);
                      }}
                    />
                  </div>
                </div>
                )}
                {!vaccineAnnualReviewLayout && !showDispenseQtyAndRefills && (
                  <>
                    <CommonSelect
                      onChange={handleDispenseQuantityChange}
                      isCreatable={true}
                      options={
                        selectedMedicine?.form?.includes("tablet")
                          ? dispenseTabletOptions
                          : selectedMedicine?.form?.includes("syr") ||
                            selectedMedicine?.form?.includes("liq")
                          ? dispenseOptions
                          : []
                      }
                      label="Dispense Quantity"
                      value={dispenseQuantity}
                      className="col-span-6 "
                      isRequired={isMAContext}
                      error={dispenseQuantityError}
                    />
                    <CustomInput
                      name="refills"
                      className="col-span-6"
                      label="Refills"
                      placeholder="Refills"
                      value={refills}
                      onChange={(e) => setRefills(e.target.value)}
                      type="text"
                      showOptionalLabel={false}
                    />
                  </>
                )}
                {!vaccineAnnualReviewLayout &&
                  showDispenseQtyAndRefills &&
                  renderSubTypeCustomFieldsBottom()}
                {!vaccineAnnualReviewLayout && (
                  <div className="col-span-12 p-1"></div>
                )}
                {!vaccineAnnualReviewLayout && (
                <CommonSelect
                  onChange={(value) => {
                    setComments(value);
                    if (value?.value !== "Other") {
                      setCustomSpecialInstruction("");
                    }
                  }}
                  isCreatable={true}
                  options={specialInstructionOptions}
                  label="Special Instructions"
                  value={comments}
                  className="col-span-12 "
                  menuPlacement="top"
                />
                )}
                {!vaccineAnnualReviewLayout && isOtherSpecialInstructionSelected && (
                  <CustomInput
                    name="customSpecialInstruction"
                    className="col-span-12"
                    label="Add Special Instruction"
                    placeholder="Enter Special Instruction"
                    value={customSpecialInstruction}
                    onChange={(e) => setCustomSpecialInstruction(e.target.value)}
                    type="text"
                    showOptionalLabel={true}
                  />
                )}
                {vaccineAnnualReviewLayout ? (
                  <div className="col-span-12 rounded-[4px] border border-gray-100 px-3 py-2.5 focus-within:border-[2px] focus-within:border-REELTeal">
                    <label
                      htmlFor="commentNotesVaccine"
                      className="block mb-1 font-normal text-gray-900 text-xs leading-[18px]"
                    >
                      Comments <span className="text-gray-600">(Optional)</span>
                    </label>
                    <textarea
                      name="commentNotes"
                      id="commentNotesVaccine"
                      rows={3}
                      value={commentNotes}
                      className="block w-full min-h-[70px] border-0 bg-transparent text-body-sm text-gray-800 placeholder:text-gray-200 p-0 focus:outline-none focus:ring-0 resize-y"
                      placeholder="Comments (Optional)"
                      onChange={(e) => setCommentNotes(e.target.value)}
                    />
                  </div>
                ) : (
                <div className="col-span-12 rounded-[4px] border border-gray-100 px-3 py-3 focus-within:border-[2px] focus-within:border-REELTeal">
                  <label
                    htmlFor="commentNotes"
                    className="block mb-1 font-normal text-gray-900 text-xs leading-[18px]"
                  >
                    Comments / Notes <span>(Optional)</span>
                  </label>
                  <textarea
                    name="commentNotes"
                    id="commentNotes"
                    value={commentNotes}
                    className="block w-full border-[0px] bg-transparent focus:outline-[0px] focus:ring-[0px] text-gray-800 placeholder:text-gray-200 p-0 focus:outline-none disabled:cursor-not-allowed"
                    placeholder="Enter Comments / Notes"
                    onChange={(e) => {
                      setCommentNotes(e.target.value);
                      adjustCommentNotesHeight(e.target);
                    }}
                  />
                  {hasCommentNotes && (
                    <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setIncludeOnPrescription((prev) => !prev)}
                        className="relative inline-flex items-center justify-center w-5 h-5 rounded-full  border border-REELTeal"
                        aria-label="Include on Prescription"
                      >
                        {includeOnPrescription && (
                          <span className="w-2.5 h-2.5 rounded-full bg-REELTeal" />
                        )}
                      </button>
                      <span className="text-sm text-gray-800">
                        Include on Prescription
                      </span>
                    </div>
                  )}
                </div>
                )}
                {!vaccineAnnualReviewLayout && (
                <CustomInput
                  name="prescriberName"
                  className="col-span-12"
                  label="Prescriber Name"
                  placeholder="Enter Prescriber Name"
                  value={prescriberName}
                  onChange={(e) => setPrescriberName(e.target.value)}
                  type="text"
                  showOptionalLabel={true}
                />
                )}
              </div>
            </div>
            {/* Action buttons */}
            <div className="flex items-center justify-center py-6">
              <div className="flex items-center justify-center space-x-5">
                <Button
                  className="min-w-[200px]"
                  text="Cancel"
                  type="primary-outline"
                  onClick={() => {
                    onClose(false);
                    resetForm();
                  }}
                />
                {!vaccineAnnualReviewLayout &&
                  (medicationName !== null &&
                  !create &&
                  !subType &&
                  !isIntervention ? (
                    <Button
                      className="min-w-[200px]"
                      text="Skip Add to Chart"
                      type="primary-outline"
                      onClick={() => {
                        onClose(false);
                        onSkip();
                        resetForm();
                      }}
                    />
                  ) : (
                    !subType &&
                    maxSteps > 1 && (
                      <Button
                        className="min-w-[200px]"
                        text="Skip & Continue"
                        type="primary"
                        onClick={() => {
                          onSkip();
                          resetForm();
                        }}
                      />
                    )
                  ))}
                {create && !subType && showSaveAndAddAnother && (
                  <Button
                    className="min-w-[200px]"
                    text="Save & Add Another"
                    type="primary-outline"
                    onClick={() => {
                      setAddAnotherDrug(true);
                      if (medicationNameInputMode) {
                        setOpenUncodedDrug(true);
                      } else {
                        manageStep(true);
                      }
                    }}
                  />
                )}
                <Button
                  className="min-w-[200px]"
                  text={
                    vaccineAnnualReviewLayout && create
                      ? maxSteps != null && step !== maxSteps
                        ? "Complete Review & Continue"
                        : "Complete Review"
                      : `${create ? "Save" : "Update"}${
                          maxSteps != null && step !== maxSteps
                            ? " & Continue"
                            : ""
                        }`
                  }
                  type="primary"
                  onClick={() => {
                    setAddAnotherDrug(false);
                    if (medicationNameInputMode) {
                      setOpenUncodedDrug(true);
                    } else {
                      manageStep(false);
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <ConfirmationModalNew
            open={openUncodedDrug}
            title={"Uncoded Drug Alert"}
            type={"primary"}
            confirmText={"Continue"}
            cancelText="Cancel & Review"
            content={() => (
              <div className="flex flex-col  text-gray-700">
                <p className="text-body-sm ">
                  An uncoded DRUG has been added.
                  <br />
                  Profile Analysis can not be performed.
                </p>
                <p className="text-body-sm mt-4">
                  A manual clinical check will be required.
                </p>
              </div>
            )}
            onCross={() => {
              setOpenUncodedDrug(false);
            }}
            handleClose={() => {
              setOpenUncodedDrug(false);
            }}
            confirmAction={() => {
              setOpenUncodedDrug(false);
              if (addAnotherDrug) {
                manageStep(true);
              } else {
                manageStep(false);
              }
            }}
          />
        </div>
      )}
      </div>
    </CommonSlider>
    </>
  );
}
